import React, { useState, useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  Heading,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Progress,
  Divider
} from '@chakra-ui/react';
import { policiaMunicipalApi, MultaPolicia } from '../../services/policiaMunicipalApi';

interface EstadisticasData {
  totalInfracciones: number;
  infraccionesPendientes: number;
  infraccionesPagadas: number;
  infraccionesVencidas: number;
  vehiculosAsegurados: number;
  montoTotal: number;
  montoPendiente: number;
  oficialMasActivo: string;
  infraccionesPorMes: { [key: string]: number };
  tiposGarantia: { [key: string]: number };
}

const EstadisticasInfracciones: React.FC = () => {
  const [estadisticas, setEstadisticas] = useState<EstadisticasData>({
    totalInfracciones: 0,
    infraccionesPendientes: 0,
    infraccionesPagadas: 0,
    infraccionesVencidas: 0,
    vehiculosAsegurados: 0,
    montoTotal: 0,
    montoPendiente: 0,
    oficialMasActivo: '',
    infraccionesPorMes: {},
    tiposGarantia: {}
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calcularEstadisticas = async () => {
      try {
        const infracciones = await policiaMunicipalApi.getMultas();
        
        const stats: EstadisticasData = {
          totalInfracciones: infracciones.length,
          infraccionesPendientes: infracciones.filter(i => i.estatus === 'PENDIENTE').length,
          infraccionesPagadas: infracciones.filter(i => i.estatus === 'PAGADA').length,
          infraccionesVencidas: infracciones.filter(i => i.estatus === 'VENCIDA').length,
          vehiculosAsegurados: infracciones.filter(i => i.tipoGarantia === 'vehiculo').length,
          montoTotal: infracciones.reduce((sum, i) => sum + (Number(i.monto) || 0), 0),
          montoPendiente: infracciones
            .filter(i => i.estatus === 'PENDIENTE')
            .reduce((sum, i) => sum + (Number(i.monto) || 0), 0),
          oficialMasActivo: '',
          infraccionesPorMes: {},
          tiposGarantia: {}
        };

        // Calcular oficial más activo
        const oficialesCount: { [key: string]: number } = {};
        infracciones.forEach(i => {
          const oficial = i.nombreOficial || i.gradoOficial;
          if (oficial) {
            oficialesCount[oficial] = (oficialesCount[oficial] || 0) + 1;
          }
        });
        
        const oficialMasActivo = Object.entries(oficialesCount)
          .sort(([,a], [,b]) => b - a)[0];
        stats.oficialMasActivo = oficialMasActivo ? 
          `${oficialMasActivo[0]} (${oficialMasActivo[1]} infracciones)` : 
          'Sin datos';

        // Calcular infracciones por mes
        const mesesCount: { [key: string]: number } = {};
        infracciones.forEach(i => {
          if (i.fechaInfraccion) {
            const fecha = new Date(i.fechaInfraccion);
            const mesAno = `${fecha.getMonth() + 1}/${fecha.getFullYear()}`;
            mesesCount[mesAno] = (mesesCount[mesAno] || 0) + 1;
          }
        });
        stats.infraccionesPorMes = mesesCount;

        // Calcular tipos de garantía
        const garantiaCount: { [key: string]: number } = {};
        infracciones.forEach(i => {
          const tipo = i.tipoGarantia || 'Sin garantía';
          garantiaCount[tipo] = (garantiaCount[tipo] || 0) + 1;
        });
        stats.tiposGarantia = garantiaCount;

        setEstadisticas(stats);
      } catch (error) {
        console.error('Error al calcular estadísticas:', error);
      } finally {
        setLoading(false);
      }
    };

    calcularEstadisticas();
  }, []);

  const porcentajePagadas = estadisticas.totalInfracciones > 0 
    ? (estadisticas.infraccionesPagadas / estadisticas.totalInfracciones) * 100 
    : 0;

  const porcentajePendientes = estadisticas.totalInfracciones > 0 
    ? (estadisticas.infraccionesPendientes / estadisticas.totalInfracciones) * 100 
    : 0;

  if (loading) {
    return (
      <Box p={4} pt={50}>
        <Text>Cargando estadísticas...</Text>
      </Box>
    );
  }

  return (
    <Box p={4} pt={50} width="100%">
      <VStack spacing={6} mb={6}>
        <Heading size="lg" color="#5b182b" textAlign="center">
          Estadísticas de Seguridad Pública
        </Heading>
        <Text color="gray.600" textAlign="center">
          Análisis estadístico de infracciones de tránsito y gestión policial
        </Text>
      </VStack>

      {/* Estadísticas principales */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        <Card bg="linear-gradient(135deg, #5b182b 0%, #691f30 100%)" color="white">
          <CardBody>
            <Stat>
              <StatLabel color="whiteAlpha.800">Total de Infracciones</StatLabel>
              <StatNumber fontSize="3xl">{estadisticas.totalInfracciones}</StatNumber>
              <StatHelpText color="whiteAlpha.700">
                Registradas en el sistema
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="linear-gradient(135deg, #bb8c52 0%, #a67a47 100%)" color="white">
          <CardBody>
            <Stat>
              <StatLabel color="whiteAlpha.800">Infracciones Pendientes</StatLabel>
              <StatNumber fontSize="3xl">{estadisticas.infraccionesPendientes}</StatNumber>
              <StatHelpText color="whiteAlpha.700">
                {porcentajePendientes.toFixed(1)}% del total
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="linear-gradient(135deg, #48bb78 0%, #38a169 100%)" color="white">
          <CardBody>
            <Stat>
              <StatLabel color="whiteAlpha.800">Infracciones Pagadas</StatLabel>
              <StatNumber fontSize="3xl">{estadisticas.infraccionesPagadas}</StatNumber>
              <StatHelpText color="whiteAlpha.700">
                {porcentajePagadas.toFixed(1)}% del total
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="linear-gradient(135deg, #f56565 0%, #e53e3e 100%)" color="white">
          <CardBody>
            <Stat>
              <StatLabel color="whiteAlpha.800">Vehículos Asegurados</StatLabel>
              <StatNumber fontSize="3xl">{estadisticas.vehiculosAsegurados}</StatNumber>
              <StatHelpText color="whiteAlpha.700">
                En corralones municipales
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Estadísticas financieras */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="md" color="#5b182b">Estadísticas Financieras</Heading>
              <Divider />
              
              <HStack justify="space-between">
                <Text fontWeight="semibold">Monto Total Generado:</Text>
                <Text fontSize="xl" fontWeight="bold" color="#5b182b">
                  ${estadisticas.montoTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </Text>
              </HStack>
              
              <HStack justify="space-between">
                <Text fontWeight="semibold">Monto Pendiente de Cobro:</Text>
                <Text fontSize="xl" fontWeight="bold" color="#bb8c52">
                  ${estadisticas.montoPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </Text>
              </HStack>
              
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Progreso de Recaudación
                </Text>
                <Progress 
                  value={porcentajePagadas} 
                  colorScheme="green" 
                  size="lg" 
                  borderRadius="md"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {porcentajePagadas.toFixed(1)}% recaudado
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="md" color="#5b182b">Tipos de Garantía</Heading>
              <Divider />
              
              {Object.entries(estadisticas.tiposGarantia).map(([tipo, cantidad]) => (
                <HStack key={tipo} justify="space-between">
                  <HStack>
                    <Badge 
                      colorScheme={
                        tipo === 'vehiculo' ? 'red' : 
                        tipo === 'licencia' ? 'purple' : 
                        tipo === 'placa' ? 'blue' : 'gray'
                      }
                    >
                      {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                    </Badge>
                  </HStack>
                  <Text fontWeight="bold">{cantidad}</Text>
                </HStack>
              ))}
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Información adicional */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Heading size="md" color="#5b182b">Información Operativa</Heading>
            <Divider />
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Oficial Más Activo:
                </Text>
                <Text fontSize="lg" color="#5b182b">
                  {estadisticas.oficialMasActivo}
                </Text>
              </Box>
              
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Infracciones Vencidas:
                </Text>
                <Text fontSize="lg" color="#f56565" fontWeight="bold">
                  {estadisticas.infraccionesVencidas} infracciones
                </Text>
              </Box>
            </SimpleGrid>

            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={2}>
                Distribución por Mes (últimos registros):
              </Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                {Object.entries(estadisticas.infraccionesPorMes)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .slice(0, 6)
                  .map(([mes, cantidad]) => (
                    <Box key={mes} p={3} bg="gray.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.600">{mes}</Text>
                      <Text fontSize="xl" fontWeight="bold" color="#5b182b">{cantidad}</Text>
                    </Box>
                  ))
                }
              </SimpleGrid>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default EstadisticasInfracciones;